import { createFileRoute, useNavigate } from '@tanstack/react-router';

import { LogsScreen, type LogsTabId } from '../../features/logs/logs-screen';
import { RequirePermission } from '../../lib/session/guards';
import { ADMIN_PERMISSIONS } from '../../lib/session/permissions';

const TABS: LogsTabId[] = ['audit', 'requests', 'jobs'];

export const Route = createFileRoute('/logs/')({
  /**
   * The open tab lives in the query string, so "the audit log, requests tab" is a link
   * someone can paste into a message. Anything unrecognised falls back to `audit` rather
   * than erroring - a bad `?tab=` is a typo, not a failure.
   */
  validateSearch: (search: Record<string, unknown>): { tab: LogsTabId } => {
    const tab = search['tab'];
    return { tab: TABS.includes(tab as LogsTabId) ? (tab as LogsTabId) : 'audit' };
  },
  component: LogsPage,
});

/**
 * `/logs` — audit, requests and job failures (MASTER_PLAN M11.8, FR-ADMIN-11).
 *
 * Two keys open the page and neither is enough on its own for both halves:
 * `audit.read` for the audit log and `logs.read` for the other two. `clinic_admin` holds
 * both, and the server checks each endpoint separately, so the guard asks for `logs.read`
 * - the key that covers two of the three tabs - and the audit tab's own endpoint refuses a
 * caller who somehow lacks `audit.read` (rules §17.2, §22.2).
 */
function LogsPage() {
  const { tab } = Route.useSearch();
  const navigate = useNavigate();

  return (
    <RequirePermission keys={[ADMIN_PERMISSIONS.logsRead]}>
      <LogsScreen
        tab={tab}
        onTabChange={(next) => {
          void navigate({ to: '/logs', search: { tab: next }, replace: true });
        }}
      />
    </RequirePermission>
  );
}
